'use client';

import { invalidate, useFrame } from '@react-three/fiber';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { editorStore, ViewportRenderMode } from '@/editor/store';

export const ViewportRenderReason = {
  Initial: 'initial',
  RenderModeChanged: 'renderModeChanged',
  SceneStateChanged: 'sceneStateChanged',
  EditorStateChanged: 'editorStateChanged',
  CameraControls: 'cameraControls',
  CameraFly: 'cameraFly',
  CameraFocus: 'cameraFocus',
  TransformControls: 'transformControls',
  GizmoTween: 'gizmoTween',
  GizmoLayout: 'gizmoLayout',
  InstancedBrush: 'instancedBrush',
  SocketHelpers: 'socketHelpers',
  VFXPreview: 'vfxPreview',
  PerfStats: 'perfStats',
} as const;

export type ViewportRenderReason = (typeof ViewportRenderReason)[keyof typeof ViewportRenderReason] | (string & {});

class ViewportRenderScheduler {
  activeHolds = new Map<ViewportRenderReason, number>();
  pendingFrames = 0;
  frameSerial = 0;
  lastFrameTime = 0;
  lastReason: ViewportRenderReason | null = null;

  constructor() {
    makeObservable(this, {
      activeHolds: observable.shallow,
      pendingFrames: observable,
      frameSerial: observable,
      lastFrameTime: observable,
      lastReason: observable.ref,
      activeHoldCount: computed,
      hasActiveWork: computed,
      isIdle: computed,
      requestFrames: action.bound,
      beginHold: action.bound,
      endHold: action.bound,
      markFrameRendered: action.bound,
      clear: action.bound,
    });
  }

  get activeHoldCount(): number {
    let count = 0;
    for (const value of this.activeHolds.values()) {
      count += value;
    }
    return count;
  }

  get hasActiveWork(): boolean {
    return this.activeHoldCount > 0 || this.pendingFrames > 0;
  }

  get isIdle(): boolean {
    return !this.hasActiveWork;
  }

  requestFrames(reason: ViewportRenderReason, frameCount = 1): void {
    this.pendingFrames = Math.max(this.pendingFrames, Math.max(1, Math.ceil(frameCount)));
    this.lastReason = reason;
    invalidate();
  }

  beginHold(reason: ViewportRenderReason): void {
    this.activeHolds.set(reason, (this.activeHolds.get(reason) ?? 0) + 1);
    this.lastReason = reason;
    invalidate();
  }

  endHold(reason: ViewportRenderReason): void {
    const current = this.activeHolds.get(reason);
    if (!current) return;
    if (current <= 1) {
      this.activeHolds.delete(reason);
    } else {
      this.activeHolds.set(reason, current - 1);
    }
    this.requestFrames(reason, 1);
  }

  markFrameRendered(): void {
    this.frameSerial += 1;
    this.lastFrameTime = performance.now();
    if (this.pendingFrames > 0) {
      this.pendingFrames -= 1;
    }
  }

  clear(): void {
    this.activeHolds.clear();
    this.pendingFrames = 0;
    this.lastReason = null;
  }
}

export const viewportRenderScheduler = new ViewportRenderScheduler();

export function requestViewportRender(reason: ViewportRenderReason): void {
  viewportRenderScheduler.requestFrames(reason, 1);
}

export function requestViewportFrames(reason: ViewportRenderReason, frameCount: number): void {
  viewportRenderScheduler.requestFrames(reason, frameCount);
}

export function holdViewportRendering(reason: ViewportRenderReason): () => void {
  viewportRenderScheduler.beginHold(reason);
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    viewportRenderScheduler.endHold(reason);
  };
}

export const ViewportRenderSchedulerBridge = observer(() => {
  const renderMode = editorStore.viewportRenderMode;
  const activeHoldCount = viewportRenderScheduler.activeHoldCount;
  const pendingFrames = viewportRenderScheduler.pendingFrames;
  const previousRenderModeRef = useRef(renderMode);

  useEffect(() => {
    requestViewportFrames(ViewportRenderReason.Initial, 3);
  }, []);

  useEffect(() => {
    if (previousRenderModeRef.current === renderMode) return;
    previousRenderModeRef.current = renderMode;
    requestViewportFrames(ViewportRenderReason.RenderModeChanged, 3);
  }, [renderMode]);

  useEffect(() => {
    if (renderMode === ViewportRenderMode.OnDemand && (activeHoldCount > 0 || pendingFrames > 0)) {
      invalidate();
    }
  }, [activeHoldCount, pendingFrames, renderMode]);

  useFrame(() => {
    viewportRenderScheduler.markFrameRendered();
    if (renderMode === ViewportRenderMode.OnDemand && viewportRenderScheduler.hasActiveWork) {
      invalidate();
    }
  }, { phase: 'finish' });

  return null;
});

ViewportRenderSchedulerBridge.displayName = 'ViewportRenderSchedulerBridge';
